import React, {useCallback, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import {InvestigationDTO, StepDTO} from '../../../@types/agent-investigation';
import AgentInvestigation from './AgentInvestigation';

/**
 * Why a question could not be answered.
 *
 * <p>The service already turns a failure into a readable message, so this takes it rather than
 * unpicking an axios error that is no longer there.</p>
 */
const reason = (err: unknown): string =>
    (err instanceof Error && err.message) || 'The question could not be answered';

/** How often progress is fetched while the agent works. */
const POLL_MS = 1200;

/** Consecutive failed polls before the reader is told the answer is not coming. */
const POLLS_BEFORE_GIVING_UP = 5;

/** One exchange: what was asked, and the work that answered it. */
export interface AgentTurn<M = Record<string, unknown>, D = unknown> {
    question: string;
    investigation: InvestigationDTO<M, D>;
}

/** How a panel reaches its agent: start a question, then poll it. */
export interface AgentClient<M = Record<string, unknown>, D = unknown> {
    /**
     * Starts one turn and returns the investigation to poll. Conversation memory is the server's,
     * under a session the client keeps; the question carries no history. (Decision of 2026-09-22 for
     * the merge with issue-1259-components: the recommendation agent, which sends history from the
     * client, adopts this at merge.)
     */
    ask: (question: string) => Promise<InvestigationDTO<M, D>>;
    /** The investigation as it stands. */
    progress: (id: string) => Promise<InvestigationDTO<M, D>>;
}

export interface AgentChatPanelProps<M = Record<string, unknown>, D = unknown> {
    /** What the panel is called in its header. */
    title: string;
    /** What the reader is told before their first question. */
    intro?: string;
    /** The input's placeholder. */
    placeholder?: string;
    /** Questions offered when there is nothing in the conversation yet. */
    suggestedPrompts?: string[];
    /** The conversation so far, held by the caller so it survives the panel being closed. */
    turns: AgentTurn<M, D>[];
    onTurnsChange: (turns: AgentTurn<M, D>[]) => void;
    /** Called when the reader clears the conversation, after the turns are emptied. */
    onCleared?: () => void;
    client: AgentClient<M, D>;
    /** Something a screen offers on a step, such as putting its query onto a register. */
    renderStepAction?: (step: StepDTO<D>) => React.ReactNode;
    /** Something a screen shows under a finished answer, such as proposals to accept. */
    renderAnswerExtras?: (investigation: InvestigationDTO<M, D>, turnIndex: number) => React.ReactNode;
    /**
     * What a link inside an answer does, given its target and its text. A screen passes this to act
     * on the agent's own references, such as navigating a canvas to a node. Answers link to things
     * inside the application, so such a link is never followed.
     */
    onAnswerLink?: (href: string, text: string) => void;
    /**
     * A follow-up question a screen offers under a finished answer, or null when it has none.
     *
     * <p>It exists for the answer that stops one step short of being useful: advice the reader would
     * have to retype to act on. Offering the question as a button asks it in the same conversation,
     * so the agent still has everything it just looked up.</p>
     */
    followUp?: (investigation: InvestigationDTO<M, D>) => {label: string; question: string} | null;
    /** Whether the panel currently fills the window, and how to change that. */
    fullscreen?: boolean;
    onToggleFullscreen?: () => void;
    headerActions?: React.ReactNode;
}

/**
 * Asks an agent a question and shows the work as it happens.
 *
 * <p>Separate from the shared chat dialog on purpose. That one hands a model the page on screen and
 * takes back a paragraph, which is the right shape for the screens that use it. This one poses a
 * question, watches tools run, and ends with an answer whose every claim has a step under it, so it
 * needs a different presentation rather than another branch inside a component several screens
 * depend on.</p>
 *
 * <p>Progress is polled rather than streamed because the development proxy buffers server-sent
 * events, and a feature that only works once deployed is not finished.</p>
 *
 * <p>Lifted from the recommendation agent's panel on issue-1259-components and generalised: the
 * agent is reached through {@code client}, and what a screen adds to a step or an answer comes
 * through the render props. At merge the recommendation panel becomes a use of this one.</p>
 */
export function AgentChatPanel<M = Record<string, unknown>, D = unknown>({
    title,
    intro,
    placeholder = 'Ask a question',
    suggestedPrompts = [],
    turns,
    onTurnsChange,
    onCleared,
    client,
    renderStepAction,
    renderAnswerExtras,
    onAnswerLink,
    followUp,
    fullscreen = false,
    onToggleFullscreen,
    headerActions,
}: AgentChatPanelProps<M, D>): React.ReactElement {
    const [question, setQuestion] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [working, setWorking] = useState(false);
    const bottom = useRef<HTMLDivElement | null>(null);
    // Held so the poll can be stopped: by clearing, or by the panel going away mid-answer.
    const poll = useRef<ReturnType<typeof setInterval> | null>(null);
    // Read inside the interval, which would otherwise close over the turns it was created with.
    const latestTurns = useRef(turns);
    latestTurns.current = turns;

    useEffect(() => {
        // Guarded because not every environment implements it, and a missing scroll is not worth
        // an uncaught error in the middle of an answer.
        bottom.current?.scrollIntoView?.({behavior: 'smooth', block: 'end'});
    }, [turns, working]);

    const stopPolling = useCallback((): void => {
        if (poll.current !== null) {
            clearInterval(poll.current);
            poll.current = null;
        }
    }, []);

    useEffect(() => stopPolling, [stopPolling]);

    const replaceLast = useCallback((investigation: InvestigationDTO<M, D>): void => {
        const held = latestTurns.current;
        if (held.length === 0) {
            return;
        }
        onTurnsChange([...held.slice(0, -1), {...held[held.length - 1], investigation}]);
    }, [onTurnsChange]);

    const follow = useCallback((id: string): void => {
        stopPolling();
        // A poll can fail for a moment without the turn being over: the agent is still working and
        // still spending. Giving up on the first failure abandoned the answer it was about to give,
        // so a few misses in a row are tolerated before the reader is told anything is wrong.
        let missed = 0;
        poll.current = setInterval(async (): Promise<void> => {
            try {
                const progress = await client.progress(id);
                missed = 0;
                replaceLast(progress);
                if (progress.done) {
                    stopPolling();
                    setWorking(false);
                }
            } catch (err: unknown) {
                missed += 1;
                if (missed >= POLLS_BEFORE_GIVING_UP) {
                    stopPolling();
                    setWorking(false);
                    setError(reason(err));
                }
            }
        }, POLL_MS);
    }, [client, replaceLast, stopPolling]);

    /**
     * Throws the conversation away.
     *
     * <p>Stops any work still running first: an answer landing into a cleared conversation would
     * leave the panel waiting on something the reader has already discarded.</p>
     */
    const clearConversation = useCallback((): void => {
        stopPolling();
        setWorking(false);
        setError(null);
        setQuestion('');
        onTurnsChange([]);
        onCleared?.();
    }, [onCleared, onTurnsChange, stopPolling]);

    const send = useCallback(async (asked: string): Promise<void> => {
        const trimmed = asked.trim();
        if (!trimmed || working) {
            return;
        }
        setQuestion('');
        setError(null);
        setWorking(true);
        try {
            const started = await client.ask(trimmed);
            onTurnsChange([...latestTurns.current, {question: trimmed, investigation: started}]);
            if (started.done) {
                setWorking(false);
            } else {
                follow(started.id);
            }
        } catch (err: unknown) {
            setWorking(false);
            setError(reason(err));
        }
    }, [client, follow, onTurnsChange, working]);

    return (
        <Stack sx={{height: '100%', minHeight: 0}}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{p: 1, pl: 1.5}}>
                <AutoAwesomeIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{flex: 1}}>{title}</Typography>
                {turns.length > 0 && (
                    <IconButton
                        size="small"
                        aria-label="Clear conversation"
                        title="Clear conversation"
                        onClick={clearConversation}
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                )}
                {onToggleFullscreen && (
                    <IconButton
                        size="small"
                        aria-label={fullscreen ? 'Exit fullscreen' : 'Fill the window'}
                        title={fullscreen ? 'Exit fullscreen' : 'Fill the window'}
                        onClick={onToggleFullscreen}
                    >
                        {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                )}
                {headerActions}
            </Stack>
            <Divider />

            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5}}>
                {turns.length === 0 && (
                    <Stack spacing={1}>
                        {intro && (
                            <Typography variant="body2" color="text.secondary">{intro}</Typography>
                        )}
                        {suggestedPrompts.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Try asking:</Typography>
                                <Stack spacing={0.5} sx={{mt: 0.5}} alignItems="flex-start">
                                    {suggestedPrompts.map((prompt: string) => (
                                        <Chip
                                            key={prompt}
                                            label={prompt}
                                            size="small"
                                            variant="outlined"
                                            onClick={(): void => {
                                                void send(prompt);
                                            }}
                                            sx={{height: 'auto', '& .MuiChip-label': {whiteSpace: 'normal', py: 0.5}}}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                )}

                <Stack spacing={2}>
                    {turns.map((turn: AgentTurn<M, D>, index: number) => {
                        // Offered on the last turn only: a follow-up to an earlier answer would ask
                        // about work the conversation has since moved on from.
                        const offered = followUp && index === turns.length - 1 && turn.investigation.done
                            ? followUp(turn.investigation)
                            : null;
                        return (
                            <Stack key={`${turn.investigation.id}-${index}`} spacing={1}>
                                <Typography variant="body2" fontWeight={600}>{turn.question}</Typography>
                                <AgentInvestigation
                                    investigation={turn.investigation}
                                    renderStepAction={renderStepAction}
                                    renderAnswerExtras={renderAnswerExtras
                                        ? (investigation: InvestigationDTO<M, D>): React.ReactNode =>
                                            renderAnswerExtras(investigation, index)
                                        : undefined}
                                    onAnswerLink={onAnswerLink}
                                />
                                {offered && (
                                    <Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={working}
                                            onClick={(): void => {
                                                void send(offered.question);
                                            }}
                                        >
                                            {offered.label}
                                        </Button>
                                    </Box>
                                )}
                            </Stack>
                        );
                    })}
                </Stack>

                {error && (
                    <Typography variant="body2" color="error" sx={{mt: 1}}>{error}</Typography>
                )}
                <div ref={bottom} />
            </Box>

            <Divider />
            <Stack direction="row" spacing={1} sx={{p: 1}} alignItems="flex-end">
                <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    placeholder={placeholder}
                    value={question}
                    disabled={working}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setQuestion(event.target.value)}
                    onKeyDown={(event: React.KeyboardEvent): void => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            void send(question);
                        }
                    }}
                />
                <IconButton
                    color="primary"
                    aria-label="Ask"
                    disabled={working || !question.trim()}
                    onClick={(): void => {
                        void send(question);
                    }}
                >
                    <SendIcon />
                </IconButton>
            </Stack>
        </Stack>
    );
}

export default AgentChatPanel;
